/**
 * Чистые helpers для редактирования нот в piano roll + undo/redo-стек.
 *
 * Edit kinds:
 * - `add` — добавить ноту.
 * - `remove` — удалить ноту (по индексу).
 * - `move` — переместить ноту (новые ticks и/или midi).
 * - `resize` — изменить длительность.
 * - `velocity` — изменить velocity.
 *
 * Undo/redo-стек — до 50 шагов (Q8). Каждый `applyEdits(midi, edits)`
 * возвращает новый `Midi` и пушит обратную операцию в стек.
 */

import type { Midi, MidiNote } from "@midi-fantom-utils/midi-core";

export type NoteEdit =
  | { type: "add"; note: Omit<MidiNote, "channel"> & { channel?: MidiNote["channel"] } }
  | { type: "remove"; noteId: number }
  | { type: "move"; noteId: number; ticks?: number; midi?: number }
  | { type: "resize"; noteId: number; durationTicks: number }
  | { type: "velocity"; noteId: number; velocity: number };

export const HISTORY_LIMIT = 50;

export type EditHistory = {
  past: Midi[];
  future: Midi[];
};

export const emptyHistory = (): EditHistory => ({ past: [], future: [] });

/**
 * Применяет массив edits к `midi` (всё в одном undo-шаге). Возвращает
 * новый `Midi` + обновлённую историю (с обратным шагом в `past`).
 */
export const applyEdits = (
  midi: Midi,
  edits: ReadonlyArray<NoteEdit>,
  history: EditHistory
): { midi: Midi; history: EditHistory } => {
  if (edits.length === 0) return { midi, history };
  const next = edits.reduce(applySingle, midi);
  const past = [...history.past, midi].slice(-HISTORY_LIMIT);
  return { midi: next, history: { past, future: [] } };
};

export const undo = (
  midi: Midi,
  history: EditHistory
): { midi: Midi; history: EditHistory } | null => {
  if (history.past.length === 0) return null;
  const previous = history.past[history.past.length - 1]!;
  const past = history.past.slice(0, -1);
  const future = [midi, ...history.future].slice(0, HISTORY_LIMIT);
  return { midi: previous, history: { past, future } };
};

export const redo = (
  midi: Midi,
  history: EditHistory
): { midi: Midi; history: EditHistory } | null => {
  if (history.future.length === 0) return null;
  const next = history.future[0]!;
  const future = history.future.slice(1);
  const past = [...history.past, midi].slice(-HISTORY_LIMIT);
  return { midi: next, history: { past, future } };
};

const applySingle = (midi: Midi, edit: NoteEdit): Midi => {
  const track = midi.tracks[0];
  if (!track) return midi;
  switch (edit.type) {
    case "add": {
      const newNote: MidiNote = {
        ...edit.note,
        ticks: Math.max(0, Math.round(edit.note.ticks)),
        durationTicks: Math.max(1, Math.round(edit.note.durationTicks)),
        velocity: clampVelocity(edit.note.velocity),
        midi: clampMidi(edit.note.midi),
      };
      return replaceTrack(midi, sortNotes([...track.notes, newNote]));
    }
    case "remove": {
      const idx = edit.noteId;
      if (idx < 0 || idx >= track.notes.length) return midi;
      const next = track.notes.filter((_, i) => i !== idx);
      return replaceTrack(midi, next);
    }
    case "move": {
      const idx = edit.noteId;
      if (idx < 0 || idx >= track.notes.length) return midi;
      const old = track.notes[idx]!;
      const updated: MidiNote = {
        ...old,
        ticks: edit.ticks !== undefined ? Math.max(0, Math.round(edit.ticks)) : old.ticks,
        midi: edit.midi !== undefined ? clampMidi(edit.midi) : old.midi,
      };
      const next = track.notes.map((n, i) => (i === idx ? updated : n));
      return replaceTrack(midi, sortNotes(next));
    }
    case "resize": {
      const idx = edit.noteId;
      if (idx < 0 || idx >= track.notes.length) return midi;
      const old = track.notes[idx]!;
      const updated: MidiNote = {
        ...old,
        durationTicks: Math.max(1, Math.round(edit.durationTicks)),
      };
      const next = track.notes.map((n, i) => (i === idx ? updated : n));
      return replaceTrack(midi, next);
    }
    case "velocity": {
      const idx = edit.noteId;
      if (idx < 0 || idx >= track.notes.length) return midi;
      const old = track.notes[idx]!;
      const updated: MidiNote = { ...old, velocity: clampVelocity(edit.velocity) };
      const next = track.notes.map((n, i) => (i === idx ? updated : n));
      return replaceTrack(midi, next);
    }
    default:
      return midi;
  }
};

const replaceTrack = (midi: Midi, notes: ReadonlyArray<MidiNote>): Midi => {
  return {
    ...midi,
    tracks: midi.tracks.map((t, i) => (i === 0 ? { ...t, notes } : t)),
  };
};

const sortNotes = (notes: ReadonlyArray<MidiNote>): ReadonlyArray<MidiNote> => {
  return [...notes].sort((a, b) => a.ticks - b.ticks || a.midi - b.midi);
};

const clampMidi = (m: number): number => {
  if (!Number.isFinite(m)) return 60;
  return Math.max(0, Math.min(127, Math.round(m)));
};

const clampVelocity = (v: number): number => {
  if (!Number.isFinite(v)) return 0.8;
  return Math.max(0, Math.min(1, v));
};

/** Стабильный id ноты = её индекс в track.notes на момент операции. */
export const noteId = (idx: number): number => idx;
